import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  PageContainer, PageHeader,
  Card, StatCard as UIStatCard,
  Table, Thead, Th, Td, TRow,
  TabGroup, Muted, EmptyState,
} from "../ui";

type ReportType = "z-report" | "by-category" | "by-pack-tier" | "by-vendor" | "by-brand" | "margin" | "low-stock" | "slow-movers" | "cashier" | "daily-sales" | "card-surcharge" | "uber-eats";

export default function Reports() {
  const [activeReport, setActiveReport] = useState<ReportType>("z-report");
  const [period, setPeriod] = useState("today");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [data, setData] = useState<Record<string, unknown> | unknown[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReport();
  }, [activeReport, period, date]);

  const loadReport = async () => {
    setLoading(true);
    try {
      let response;
      switch (activeReport) {
        case "z-report":
          response = await api.get(`/reports/z-report?date=${date}`);
          break;
        case "by-category":
          response = await api.get(`/reports/sales-by-category?period=${period}`);
          break;
        case "by-pack-tier":
          response = await api.get(`/reports/sales-by-pack-tier?period=${period}`);
          break;
        case "by-vendor":
          response = await api.get(`/reports/sales-by-vendor?period=${period}`);
          break;
        case "by-brand":
          response = await api.get(`/reports/sales-by-brand?period=${period}`);
          break;
        case "margin":
          response = await api.get(`/reports/margin?period=${period}`);
          break;
        case "low-stock":
          response = await api.get("/reports/low-stock");
          break;
        case "slow-movers":
          response = await api.get("/reports/slow-movers?days=14");
          break;
        case "cashier":
          response = await api.get(`/reports/cashier-performance?period=${period}`);
          break;
        case "daily-sales":
          response = await api.get(`/reports/daily-sales?period=this_month`);
          break;
        case "card-surcharge":
          response = await api.get(`/reports/card-surcharge?period=${period}`);
          break;
        case "uber-eats": {
          // Uber's report arrives a week behind, so show the last 13 weeks whatever the period.
          const end = new Date(Date.now() + 86400000).toISOString().split("T")[0];
          const start = new Date(Date.now() - 91 * 86400000).toISOString().split("T")[0];
          response = await api.get(`/reports/uber-eats?start=${start}&end=${end}`);
          break;
        }
      }
      setData(response?.data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const reports: Array<{ key: string; label: string }> = [
    { key: "z-report", label: "Z-Report" },
    { key: "by-category", label: "By Category" },
    { key: "by-pack-tier", label: "By Pack Tier" },
    { key: "by-vendor", label: "By Vendor" },
    { key: "by-brand", label: "By Brand" },
    { key: "margin", label: "Margin" },
    { key: "low-stock", label: "Low Stock" },
    { key: "slow-movers", label: "Slow Movers" },
    { key: "cashier", label: "Cashier" },
    { key: "daily-sales", label: "Daily Sales" },
    { key: "card-surcharge", label: "Card vs Fees" },
    { key: "uber-eats", label: "Uber Eats" },
  ];

  const periods: Array<{ key: string; label: string }> = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "this_week", label: "This Week" },
    { key: "last_week", label: "Last Week" },
    { key: "this_month", label: "This Month" },
    { key: "last_month", label: "Last Month" },
  ];

  return (
    <PageContainer>
      <PageHeader title="Reports" subtitle="Sales analytics and inventory reports" />

      <TabGroup
        tabs={reports}
        active={activeReport}
        onChange={(key) => setActiveReport(key as ReportType)}
      />

      {/* Controls */}
      <div className="flex gap-3 mb-4">
        {activeReport === "z-report" ? (
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-4 py-2.5 rounded-xl outline-none"
            style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
          />
        ) : !["low-stock", "slow-movers", "uber-eats"].includes(activeReport) && (
          <TabGroup
            tabs={periods}
            active={period}
            onChange={setPeriod}
          />
        )}
      </div>

      {loading ? (
        <div className="text-center py-12"><Muted>Loading...</Muted></div>
      ) : (
        activeReport === "uber-eats"
          ? <UberEatsReport data={data as Record<string, unknown> | null} onImported={loadReport} />
          : <ReportContent type={activeReport} data={data} />
      )}
    </PageContainer>
  );
}

function ReportContent({ type, data }: { type: ReportType; data: unknown }) {
  if (!data) return (
    <Card>
      <EmptyState message="No data" />
    </Card>
  );

  const d = data as Record<string, unknown>;

  if (type === "z-report") {
    const summary = d.summary as Record<string, unknown>;
    const byCategory = (d.by_category || []) as Array<Record<string, unknown>>;
    const byCashier = (d.by_cashier || []) as Array<Record<string, unknown>>;

    return (
      <div>
        <Card className="mb-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>Z-Report: {d.date as string}</h2>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <UIStatCard label="Total Sales" value={`$${Number(summary?.total_sales || 0).toFixed(2)}`} />
            <UIStatCard label="Transactions" value={String(summary?.transaction_count || 0)} />
            <UIStatCard label="Cash" value={`$${Number(summary?.cash_sales || 0).toFixed(2)}`} variant="gold" />
            <UIStatCard label="Card" value={`$${Number(summary?.credit_sales || 0).toFixed(2)}`} variant="blue" />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>By Category</h3>
              <DataTable
                headers={["Category", "Units", "Revenue"]}
                rows={byCategory.map((r) => [
                  String(r.category), String(r.units_sold), `$${Number(r.revenue || 0).toFixed(2)}`
                ])}
              />
            </div>
            <div>
              <h3 className="font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>By Cashier</h3>
              <DataTable
                headers={["Cashier", "Txns", "Sales"]}
                rows={byCashier.map((r) => [
                  String(r.cashier_name), String(r.transactions), `$${Number(r.total_sales || 0).toFixed(2)}`
                ])}
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Card surcharge report
  if (type === "card-surcharge") {
    const summary = d.summary as Record<string, unknown>;
    const daily = (d.daily || []) as Array<Record<string, unknown>>;

    if (!summary) return <Card><EmptyState message="No card transaction data for this period" /></Card>;

    const surcharge = Number(summary.total_surcharge_collected) || 0;
    const estFees = Number(summary.estimated_processing_fees) || 0;
    const netProfit = Number(summary.net_card_profit) || 0;
    const coveragePct = Number(summary.coverage_pct) || 0;

    return (
      <div>
        <Card className="mb-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>Card Surcharge vs Processing Fees</h2>
          <div className="grid grid-cols-4 gap-4 mb-4">
            <UIStatCard label="Card Revenue" value={`$${Number(summary.card_revenue || 0).toFixed(2)}`} />
            <UIStatCard label="Surcharge Collected" value={`$${surcharge.toFixed(2)}`} variant="gold" />
            <UIStatCard label="Est. Processing Fees" value={`$${estFees.toFixed(2)}`} variant={estFees > surcharge ? "danger" : "default"} />
            <UIStatCard
              label="Net Card Profit"
              value={`${netProfit >= 0 ? "+" : ""}$${netProfit.toFixed(2)}`}
              variant={netProfit >= 0 ? "green" : "danger"}
            />
          </div>
          <div className="rounded-xl p-4 mb-4" style={{ background: netProfit >= 0 ? "#f0fdf4" : "#fef2f2", border: `1px solid ${netProfit >= 0 ? "#bbf7d0" : "#fecaca"}` }}>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{netProfit >= 0 ? "\u2705" : "\u274c"}</span>
              <div>
                <div className="font-bold" style={{ color: netProfit >= 0 ? "#16a34a" : "#dc2626" }}>
                  {netProfit >= 0
                    ? `Surcharge covers ${coveragePct}% of estimated fees — you're making money on cards!`
                    : `Surcharge only covers ${coveragePct}% of estimated fees — you're losing $${Math.abs(netProfit).toFixed(2)} on cards`
                  }
                </div>
                <div className="text-xs mt-1" style={{ color: "var(--rbw-gray-dark)" }}>
                  Compare the "Surcharge Collected" against your actual Helcim monthly statement for exact numbers. Estimates use average interchange rates.
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="rounded-lg p-3" style={{ background: "var(--rbw-bg)" }}>
              <div style={{ color: "var(--rbw-gray-dark)" }}>Card Transactions</div>
              <div className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>{String(summary.card_transactions)}</div>
            </div>
            <div className="rounded-lg p-3" style={{ background: "var(--rbw-bg)" }}>
              <div style={{ color: "var(--rbw-gray-dark)" }}>Cash Transactions</div>
              <div className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>{String(summary.cash_transactions)}</div>
            </div>
            <div className="rounded-lg p-3" style={{ background: "var(--rbw-bg)" }}>
              <div style={{ color: "var(--rbw-gray-dark)" }}>Card % of Revenue</div>
              <div className="text-xl font-bold" style={{ color: "var(--rbw-navy)" }}>{Number(summary.card_pct || 0).toFixed(1)}%</div>
            </div>
          </div>
        </Card>

        {daily.length > 0 && (
          <Card>
            <h3 className="text-lg font-bold mb-3 px-4 pt-4" style={{ color: "var(--rbw-navy)" }}>Daily Breakdown</h3>
            <Table>
              <Thead>
                <Th>Date</Th>
                <Th align="right">Card Txns</Th>
                <Th align="right">Card Revenue</Th>
                <Th align="right">Surcharge</Th>
                <Th align="right">If Cash</Th>
              </Thead>
              <tbody>
                {daily.map((row, i) => (
                  <TRow key={i} index={i}>
                    <Td>{new Date(row.date as string).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</Td>
                    <Td align="right">{String(row.card_transactions)}</Td>
                    <Td align="right" bold>${Number(row.card_revenue).toFixed(2)}</Td>
                    <Td align="right">
                      <span style={{ color: "var(--rbw-success)" }}>+${Number(row.surcharge_collected).toFixed(2)}</span>
                    </Td>
                    <Td align="right"><Muted>${Number(row.equivalent_cash_revenue).toFixed(2)}</Muted></Td>
                  </TRow>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
      </div>
    );
  }

  // Generic table reports
  const rows = (d.data || d) as Array<Record<string, unknown>>;
  if (!Array.isArray(rows) || rows.length === 0) {
    return (
      <Card>
        <EmptyState message="No data for this period" />
      </Card>
    );
  }

  const headers = Object.keys(rows[0]).filter((k) => k !== "id" && k !== "product_id");

  return (
    <Table>
      <Thead>
        {headers.map((h) => (
          <Th key={h}>{h.replace(/_/g, " ")}</Th>
        ))}
      </Thead>
      <tbody>
        {rows.map((row, i) => (
          <TRow key={i} index={i}>
            {headers.map((h) => (
              <Td key={h}>{formatValue(row[h])}</Td>
            ))}
          </TRow>
        ))}
      </tbody>
    </Table>
  );
}

function DataTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--rbw-border)" }}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: "var(--rbw-bg)" }}>
            {headers.map((h) => (
              <th key={h} className="text-left px-3 py-2 text-xs uppercase font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: "1px solid var(--rbw-border)" }}>
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-1.5" style={{ color: "var(--rbw-navy)" }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return "-";
  if (typeof val === "number") {
    if (val > 100 || String(val).includes(".")) return Number(val).toFixed(2);
    return String(val);
  }
  if (typeof val === "string" && val.match(/^\d{4}-\d{2}-\d{2}T/)) {
    return new Date(val).toLocaleString();
  }
  return String(val);
}

interface UberImportSummary {
  orders: number; alreadyImported: number; newOrders: number; linesDeducted: number;
  linesNotDeducted: { order: string; item: string; why: string }[];
  salesExclTax: number; payout: number; committed: boolean;
}

/**
 * Uber Eats orders are filled from the Uber handheld, never rung at the register
 * (Uber collects and pays the sales tax). Once a week, import Uber's Items report
 * here: each order is taken once and its items come off stock.
 */
function UberEatsReport({ data, onImported }: { data: Record<string, unknown> | null; onImported: () => void }) {
  const [file, setFile] = useState<{ name: string; csv: string } | null>(null);
  const [preview, setPreview] = useState<UberImportSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const weeks = ((data?.weeks || []) as Array<Record<string, unknown>>);
  const items = ((data?.items || []) as Array<Record<string, unknown>>);
  const through = data?.imported_through as string | null | undefined;
  const money = (v: unknown) => `$${Number(v || 0).toFixed(2)}`;

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setPreview(null);
    setError("");
    if (!f) { setFile(null); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, csv: String(reader.result || "") });
    reader.readAsText(f);
  };

  const send = async (commit: boolean) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/reports/uber-eats/import", { csv: file.csv, filename: file.name, commit });
      setPreview(r.data);
      if (commit) { setFile(null); onImported(); }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      setError(e.response?.data?.error || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Card className="mb-6">
        <h2 className="text-xl font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>Import Uber's Items report</h2>
        <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
          Uber Eats Manager &gt; Reports &gt; Items, download as CSV. Orders already in Karen are skipped, so overlapping weeks are fine.
          {through ? ` Imported through ${new Date(through).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}.` : " Nothing imported yet."}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept=".csv,text/csv" onChange={pick} />
          <button disabled={!file || busy} onClick={() => send(false)}
            className="px-4 py-2 rounded-xl font-semibold disabled:opacity-40"
            style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}>Preview</button>
          <button disabled={!file || busy || !preview || preview.committed} onClick={() => send(true)}
            className="px-4 py-2 rounded-xl font-semibold text-white disabled:opacity-40"
            style={{ background: "var(--rbw-navy)" }}>Import</button>
        </div>
        {error && <div className="mt-3 text-sm" style={{ color: "var(--rbw-danger, #dc2626)" }}>{error}</div>}
        {preview && (
          <div className="mt-4 rounded-xl p-4 text-sm" style={{ background: "var(--rbw-bg)", color: "var(--rbw-navy)" }}>
            <div className="font-bold mb-1">{preview.committed ? "Imported" : "Preview, nothing saved yet"}</div>
            <div>{preview.newOrders} new order(s), {preview.alreadyImported} already in Karen.
              New sales {money(preview.salesExclTax)} before tax, {money(preview.payout)} paid out.
              {" "}{preview.linesDeducted} item line(s) {preview.committed ? "came" : "will come"} off stock.</div>
            {preview.linesNotDeducted.length > 0 && (
              <ul className="mt-2 list-disc pl-5">
                {preview.linesNotDeducted.map((l, i) => <li key={i}>{l.order} {l.item}: not taken off stock ({l.why})</li>)}
              </ul>
            )}
          </div>
        )}
      </Card>

      <Card className="mb-6">
        <h3 className="text-lg font-bold mb-3" style={{ color: "var(--rbw-navy)" }}>By week, last 13 weeks</h3>
        <p className="text-xs mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
          Not in register sales or the PA-3: Uber collects the sales tax and pays it to the state.
        </p>
        {weeks.length === 0 ? <EmptyState message="No Uber Eats orders imported for these weeks" /> : (
          <DataTable
            headers={["Week of", "Orders", "Sales", "Tax (Uber paid)", "Uber fees", "Other", "Payout"]}
            rows={weeks.map((w) => [
              String(w.week_of), `${w.orders}${Number(w.cancelled) ? ` (+${w.cancelled} cancelled)` : ""}`,
              money(w.sales), money(w.tax_paid_by_uber), money(w.uber_fees), money(w.other), money(w.payout),
            ])}
          />
        )}
      </Card>

      {items.length > 0 && (
        <Card>
          <h3 className="text-lg font-bold mb-3" style={{ color: "var(--rbw-navy)" }}>What sold on Uber</h3>
          <DataTable
            headers={["Item", "Units", "Sales"]}
            rows={items.map((r) => [String(r.item), String(Number(r.units)), money(r.sales)])}
          />
        </Card>
      )}
    </div>
  );
}
